import request from '../utils/request';

/**
 * 自研表单系统 · 后台接口封装
 * 契约来源：yanhuo_applet/docs/表单管理方案_20261010.md 第六节 B（14 个接口）
 * 说明：所有写接口按项目既有约定用 JSON body 提交，读/导出接口用 params。
 */

const jsonHeaders = {
    'Content-Type': 'application/json;charset=UTF-8',
};

/* ============================== 状态字典（供四页复用，保持口径一致） ============================== */

// 表单状态：0 草稿 / 1 已发布 / 2 已结束（后端 XfForm.status 只允许 0~2，无「停用」）
export const FORM_STATUS_TEXT = {
    0: '草稿',
    1: '已发布',
    2: '已结束',
};

// 提交记录处理状态：0 待处理 / 1 已处理 / 2 已作废
export const RECORD_STATUS_TEXT = {
    0: '待处理',
    1: '已处理',
    2: '已作废',
};

// 重复提交限制口径（与契约 settings.limitType 对齐）
export const LIMIT_TYPE_OPTIONS = [
    { value: 'none', label: '不限制' },
    { value: 'phone_once', label: '同一手机号仅一次' },
    { value: 'phone_day_once', label: '同一手机号每日一次' },
    { value: 'phone_student_once', label: '同一学员仅一次' },
];

// 「选项库」分组标识（通用：只是几个常用/示例键，实际可在设计器「选项来源 → 选项库」里现场新建）
// ★ 2026-10-12 通用化：这里只是**表单系统自有**的选项分组，与业务表无关；标签用中性词。
// ⚠️ school 仅为兼容历史数据保留。
export const GROUP_KEY_OPTIONS = [
    { value: 'school', label: '学校(历史)' },
    { value: 'project', label: '项目' },
];

/* ============================== ① 表单定义 ============================== */

/** 1. 表单列表（分页 + 关键字/状态筛选 + 数据权限） */
export const formPage = (param) => {
    return request({
        url: '/sys/form/page',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 2. 表单详情（含 schema） */
export const formDetail = (id) => {
    return request({
        url: '/sys/form/detail',
        method: 'GET',
        params: { id },
    });
};

/** 3. 新建/更新（含 schema_json、settings_json、city_ids） */
export const formSave = (param) => {
    return request({
        url: '/sys/form/save',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 4. 发布 / 停用 / 结束 */
export const formStatus = (id, status) => {
    return request({
        url: '/sys/form/status',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id, status }),
        headers: jsonHeaders,
    });
};

/** 5. 逻辑删除 */
export const formDelete = (id) => {
    return request({
        url: '/sys/form/delete',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id }),
        headers: jsonHeaders,
    });
};

/** 6. 复制表单（返回新表单 id） */
export const formCopy = (id) => {
    return request({
        url: '/sys/form/copy',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id }),
        headers: jsonHeaders,
    });
};

/** 6b. 适用城市下拉（直营城市，返回 [{id,name}]） */
export const formCities = (keyword) => {
    return request({
        url: '/sys/form/cities',
        method: 'GET',
        params: { keyword },
    });
};

/* ============================== ② 提交记录 ============================== */

/** 7. 提交数据列表（按城市/学校/项目/时间/状态筛选） */
export const formRecordPage = (param) => {
    return request({
        url: '/sys/form/record/page',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 8. 单条记录详情 */
export const formRecordDetail = (id) => {
    return request({
        url: '/sys/form/record/detail',
        method: 'GET',
        params: { id },
    });
};

/** 9. 改处理状态 / 备注 */
export const formRecordStatus = (param) => {
    return request({
        url: '/sys/form/record/status',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/**
 * 10. 导出 Excel
 * ⚠ 后端实现是「生成 xlsx → 上传 MinIO → 返回下载直链」，**不是 arraybuffer**：
 *    入参为 XfFormRecordPageParam，出参 data = { url, name, rows }。
 *    前端拿到 url 后直接触发浏览器下载（或提示到下载中心查看）。
 */
export const formRecordExport = (param) => {
    return request({
        url: '/sys/form/record/export',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 11. 统计（按城市/学校/项目/日期聚合） */
export const formRecordStatistics = (param) => {
    return request({
        url: '/sys/form/record/statistics',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/* ============================== ③ 自定义数据源 ============================== */

/** 12. 数据源选项列表 */
export const formOptionList = (param) => {
    return request({
        url: '/sys/form/option/list',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 13. 数据源选项 单条新增/修改（入参 = XfFormOption 单个对象） */
export const formOptionSave = (param) => {
    return request({
        url: '/sys/form/option/save',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 13b. 数据源选项 批量新增（一行一个，入参 {groupKey, cityId, labels:[...] }） */
export const formOptionBatch = (param) => {
    return request({
        url: '/sys/form/option/batch',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 14b. 触发记录（回调与推送的执行日志） */
export const formHookLog = (param) => {
    return request({
        url: '/sys/form/hook/log',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 14c. 手动重推某条提交的触发（channel 可空 = 重推全部已开启通道） */
export const formHookRetry = (recordId, channel) => {
    return request({
        url: '/sys/form/hook/retry',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ recordId, channel }),
        headers: jsonHeaders,
    });
};

/** 14d. 测试发送（用模拟数据打一遍配置，返回 {ok, message, details[]}） */
export const formHookTest = (formId, channel) => {
    return request({
        url: '/sys/form/hook/test',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ formId, channel }),
        headers: jsonHeaders,
    });
};

/** 15. 删除选项 */
export const formOptionDelete = (id) => {
    return request({
        url: '/sys/form/option/delete',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id }),
        headers: jsonHeaders,
    });
};

/* ==========================================================================================
 * v2.2 管理态补齐（分组 / 回收站 / 主题库 / 模板中心）
 * 对照 tduck 涂鸦表单补齐「管理态」，但触达更强：分组+回收站为自研，主题/模板均支持
 * 「一键套用到现有表单 / 从现有表单沉淀为模板」的双向流转，而不只是单向新建。
 * ========================================================================================== */

/* ---------------- 16. 表单分组 ---------------- */

/** 16a. 分组列表（后端已把「分组下表单数 / 累计提交量」一次算好，前端不用再各自拉） */
export const formGroupList = () => {
    return request({
        url: '/sys/form/group/list',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({}),
        headers: jsonHeaders,
    });
};

/** 16b. 分组 新建/修改（带 id 即修改） */
export const formGroupSave = (param) => {
    return request({
        url: '/sys/form/group/save',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 16c. 分组删除（组内表单自动回落「未分组」，不会删表单） */
export const formGroupDelete = (id) => {
    return request({
        url: '/sys/form/group/delete',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id }),
        headers: jsonHeaders,
    });
};

/** 16d. 批量把表单移动到某分组（groupId = 0 表示移到未分组） */
export const formGroupMove = (formIds, groupId) => {
    return request({
        url: '/sys/form/group/move',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ formIds, groupId }),
        headers: jsonHeaders,
    });
};

/* ---------------- 17. 回收站 ---------------- */

/** 17a. 回收站分页 */
export const formRecyclePage = (param) => {
    return request({
        url: '/sys/form/recycle/page',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param || {}),
        headers: jsonHeaders,
    });
};

/** 17b. 回收站恢复（支持单条 id 或批量 ids） */
export const formRecycleRestore = (ids) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    return request({
        url: '/sys/form/recycle/restore',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ ids: arr }),
        headers: jsonHeaders,
    });
};

/** 17c. 彻底删除（不可恢复；连同提交记录与触发日志一起清） */
export const formRecyclePurge = (ids) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    return request({
        url: '/sys/form/recycle/delete',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ ids: arr }),
        headers: jsonHeaders,
    });
};

/** 17d. 全局概览（总数/已发布/草稿/回收站/浏览量/提交量）—— 列表页顶部概览卡用 */
export const formOverview = () => {
    return request({
        url: '/sys/form/overview',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({}),
        headers: jsonHeaders,
    });
};

/* ---------------- 18. 主题库（可复用外观） ---------------- */

/** 18a. 主题列表（返回 {list, styles}，styles 是风格标签，供筛选条用） */
export const formThemeList = (style) => {
    return request({
        url: '/sys/form/theme/list',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ style: style || null }),
        headers: jsonHeaders,
    });
};

/** 18b. 主题 新建/修改 */
export const formThemeSave = (param) => {
    return request({
        url: '/sys/form/theme/save',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 18c. 主题删除（内置预设不可删） */
export const formThemeDelete = (id) => {
    return request({
        url: '/sys/form/theme/delete',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id }),
        headers: jsonHeaders,
    });
};

/** 18d. 把主题套用到某张表单（写入该表单外观配置；返回 {ok, message, appearance}） */
export const formThemeApply = (themeId, formId) => {
    return request({
        url: '/sys/form/theme/apply',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ themeId, formId }),
        headers: jsonHeaders,
    });
};

/* ---------------- 19. 模板中心 ---------------- */

/** 19a. 模板分页（不含 schema 大字段，列表很轻） */
export const formTemplatePage = (param) => {
    return request({
        url: '/sys/form/template/page',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param || {}),
        headers: jsonHeaders,
    });
};

/** 19b. 模板分类标签 */
export const formTemplateCategories = () => {
    return request({
        url: '/sys/form/template/categories',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({}),
        headers: jsonHeaders,
    });
};

/** 19c. 模板详情（含字段结构，供预览弹窗） */
export const formTemplateDetail = (id) => {
    return request({
        url: '/sys/form/template/detail',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id }),
        headers: jsonHeaders,
    });
};

/** 19d. 模板 新建/修改 */
export const formTemplateSave = (param) => {
    return request({
        url: '/sys/form/template/save',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 19e. 把已有表单「另存为模板」 */
export const formTemplateSaveAs = (param) => {
    return request({
        url: '/sys/form/template/saveAs',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 19f. 模板删除 */
export const formTemplateDelete = (id) => {
    return request({
        url: '/sys/form/template/delete',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id }),
        headers: jsonHeaders,
    });
};

/** 19g. 模板 上架/下架 */
export const formTemplateStatus = (id, status) => {
    return request({
        url: '/sys/form/template/status',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id, status }),
        headers: jsonHeaders,
    });
};

/** 19h. 用模板创建新表单（产出草稿，返回 {formId}） */
export const formTemplateUse = (templateId, title, groupId) => {
    return request({
        url: '/sys/form/template/use',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ templateId, title, groupId }),
        headers: jsonHeaders,
    });
};

/* ==========================================================================================
 * v3.17 核销凭证 + 审批管理（后台视角）
 * 核销码 = 提交成功后发给家长的凭证；员工当面扫码/输码核销。
 * 审批是表单级可选项（未开启审批的表单不会产生审批单）。
 * ========================================================================================== */

/* ---------------- 20. 核销凭证 ---------------- */

// 核销状态：0 未核销 / 1 已核销 / 2 已作废（与后端 verifyState 口径一致）
export const VERIFY_STATE_TEXT = {
    0: '未核销',
    1: '已核销',
    2: '已作废',
};

/**
 * 20a. 核销记录分页
 * 入参：{ pageIndex, pageSize, formId?, formKey?, verifyState?, keyword?, areaIdList?[] }
 *   verifyState 不传 = 全部；keyword 同时模糊匹配 学员姓名 / 手机号 / 核销码。
 * 服务端已强制「登录人管辖校区 ∩ 请求校区」，前端传 areaIdList 不能越权。
 */
export const formVerifyPage = (param) => {
    return request({
        url: '/sys/form/verify/page',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 20b. 作废核销码（仅未核销可作废，失败会给中文原因） */
export const formVerifyVoid = (id) => {
    return request({
        url: '/sys/form/verify/void',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify({ id }),
        headers: jsonHeaders,
    });
};

/**
 * 20c. 手动补发核销码
 * 入参 { id } 补单条；{ formId } 给该表单下所有「尚未打码」的记录批量补发。
 * 批量时 data = { count: n }，message 形如「已补发 N 条」。
 */
export const formVerifyReissue = (param) => {
    return request({
        url: '/sys/form/verify/reissue',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/* ---------------- 21. 审批管理 ---------------- */

// 审批实例状态：1 审批中 / 2 已通过 / 3 已驳回 / 4 已撤回
export const APPROVAL_STATUS_TEXT = {
    1: '审批中',
    2: '已通过',
    3: '已驳回',
    4: '已撤回',
};

// el-tag 的 type（与 APPROVAL_STATUS_TEXT 一一对应，供页面复用、口径统一）
export const APPROVAL_STATUS_TYPE = {
    1: 'warning',
    2: 'success',
    3: 'danger',
    4: 'info',
};

/**
 * 21a. 审批实例分页
 * 入参：{ pageIndex, pageSize, formId?, status?, startDate?, endDate?, keyword?, areaIdList?[] }
 *   keyword 模糊匹配提交人姓名；startDate/endDate 格式 yyyy-MM-dd。
 */
export const formApprovalPage = (param) => {
    return request({
        url: '/sys/form/approval/page',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/**
 * 21b. 审批详情
 * 出参 data：{ instance, steps:[{step,name,mode}], logs:[{...action...}] }
 *   steps 是审批链（step 从 1 开始；mode==='all' 表示需全部审批人通过）。
 *   logs 按 id 升序（时间轴正序），action 取值见后端 XfFormApprovalLog 常量。
 */
export const formApprovalDetail = (id) => {
    return request({
        url: '/sys/form/approval/detail',
        method: 'GET',
        params: { id },
    });
};

/* ============================== v3.17 新增（契约 §55.3 后台部分） ==============================
 * ★ 一律挂 /sys/form/** 前缀 ⇒ 依 RequestDetailFilter 前缀白名单，sys 身份天然放行，
 *   无需改 filter / 加白名单 / 改 yml。
 * 说明：读接口走 GET + params；写接口（开奖 / 名额保存）走 POST + JSON body。
 * ⚠️ 拼团接口是 /sys/form/group/page，与「选项库」的 /sys/form/group/list|save|delete|move
 *    **不是同一套**（路径不同、语义不同），命名上刻意用 formGroupPage 区分。
 */

/** 22. 签署留痕列表（GET /sys/form/sign/page）
 *  入参：{ pageIndex, pageSize, formId?, keyword?, startDate?, endDate? }
 *  出参 records 关键列：recordId / signerName / agreementTitle / snapshotUrl / snapshotHash / dataHash / signImage / signedAt */
export const formSignPage = (param) => {
    return request({
        url: '/sys/form/sign/page',
        method: 'GET',
        params: param,
    });
};

/** 23. 拼团列表（GET /sys/form/group/page）
 *  入参：{ pageIndex, pageSize, formId?, state?, keyword? }
 *  出参 records 关键列：groupNo / leaderRecordId / memberCount / minMembers / maxMembers / state / deadline / createdAt
 *  state：1 招募中 / 2 已成团 / 3 已失败 */
export const formGroupPage = (param) => {
    return request({
        url: '/sys/form/group/page',
        method: 'GET',
        params: param,
    });
};

/** 24. 抽签结果列表（GET /sys/form/lottery/page）
 *  入参：{ pageIndex, pageSize, formId? }
 *  出参 records 关键列：recordId / isWin / signerName? / drawnAt / seed? / state? */
export const formLotteryPage = (param) => {
    return request({
        url: '/sys/form/lottery/page',
        method: 'GET',
        params: param,
    });
};

/** 25. 开奖（POST /sys/form/lottery/draw）入参 { formId }
 *  ★ 幂等：已开奖（state=1）再开直接返回既有结果，不会重抽。 */
export const formLotteryDraw = (param) => {
    return request({
        url: '/sys/form/lottery/draw',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 26. 保存名额配置（POST /sys/form/quota/save）
 *  入参：{ formId, total, startAt, endAt } —— 同步初始化/更新名额计数器行。 */
export const formQuotaSave = (param) => {
    return request({
        url: '/sys/form/quota/save',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

// 拼团状态字典（与后端 xf_form_group_sign.state 对齐）
export const GROUP_SIGN_STATE_TEXT = {
    1: '招募中',
    2: '已成团',
    3: '已失败',
};

/* ==================== 分发二维码（方形二维码 + 自定义 logo） ====================
 * 为什么不用官方「小程序二维码」：该接口的 path 不能携带 query ⇒ 无法区分不同表单，
 * 官方文档自己也标注「不推荐使用」。所以走官方「扫普通链接二维码打开小程序」：
 * 二维码内容 = linkPrefix + formKey，由小程序后台配置的**前缀规则**决定扫码后跳哪个页面。
 * ⚠️ 使用前提：小程序后台已配置并**发布**该前缀规则，否则二维码扫开只会打开网页。
 */

/** 27. 生成带 logo 的方形二维码（POST /sys/form/qrcode/link）
 *  入参：{ id, logoUrl, size }
 *  返回 data：{ qrImgUrl, qrContent, formKey, qrType, logoUrl, logoApplied, size, rulePage, v }
 *  ★ logoApplied=false 表示本次未成功加 logo（已降级为纯二维码），前端必须如实提示；
 *  ★ qrImgUrl 固定不变，改图后靠 data.v 破缓存。 */
export const formQrLink = (param) => {
    return request({
        url: '/sys/form/qrcode/link',
        method: 'POST',
        dataType: 'json',
        data: JSON.stringify(param),
        headers: jsonHeaders,
    });
};

/** 28. 读取表单二维码分发偏好（GET /sys/form/qrcode/config?id=）
 *  返回 data：{ qrType, qrLogo, qrImage, qrContent, formKey, linkPrefix, rulePage, suggestContent } */
export const formQrConfig = (id) => {
    return request({
        url: '/sys/form/qrcode/config',
        method: 'get',
        params: { id },
    });
};
